// The gate every bundle surface renders behind, and the wording it uses when the catalogues
// come back empty.
//
// Three surfaces ask the same two questions before drawing anything — "have both catalogues
// actually come back?" and "did they come back EMPTY?" — and each used to answer them
// locally. The approval wizard's copy was still the length-based one, which cannot tell
// "never fetched" from "fetched and genuinely empty": on a system with no live bundles it
// showed a skeleton for ever, and gave the admin no reason why the company could not be
// approved. Answering it in one place is what stops the copies drifting again.

// The dropdown's "None" row, and the value a company with no bundle reads as.
export const NO_BUNDLE = -1;

// One wording, because it is one fact: only Access Bundles can fix it.
export const NO_BUNDLES_HINT = " There are no active bundles to choose from yet"
  + " — create one under Access Bundles first.";

// A live bundle whose modules cannot be drawn. Rendering the grid here would be a blank box
// with no explanation.
export const NO_MODULES_MESSAGE = "No modules are available to show. This company keeps the"
  + " access rights its bundle already grants.";

// Nothing ever arrived, so there is nothing to draw and no point waiting.
export const CATALOGUE_UNAVAILABLE_MESSAGE = "The bundle catalogue could not be loaded, so"
  + " the modules cannot be shown. Nothing has been changed.";

// `isLoaded` is the FACT that both catalogues have responded (companies.bundleOptionsLoaded
// and bundles.moduleListingLoaded), never their length. `isLoading` tracks getBundleOptions:
// a list being refetched is stale enough that "this bundle is no longer active" must not be
// said over it. `hasFailed` is companies.bundleOptionsFailed || bundles.moduleListingFailed.
export const describeCatalogue = ({
  isLoaded,
  isLoading,
  hasFailed,
  bundleOptions,
  moduleListing,
}) => {
  const loaded = Boolean(isLoaded);
  return {
    // Nothing may be asserted until this is true. A skeleton is the honest answer before it.
    isReady: loaded && !isLoading,
    // Never arrived AND the read that would have delivered it failed: the one case where a
    // skeleton would sit there for ever. Deliberately requires `!loaded` — a failed REFRESH
    // of a list that did arrive leaves the cached one on screen, which is what a save
    // followed by a failed cache warm-up must not disturb.
    hasFailedToLoad: !loaded && Boolean(hasFailed) && !isLoading,
    // Fetched, and the system really has no live bundles.
    hasNoBundles: loaded && (bundleOptions || []).length === 0,
    // Fetched, and the module catalogue really is empty.
    hasNoModules: loaded && (moduleListing || []).length === 0,
  };
};
